<template>
	<div class="income-info">
		<section>
			<div class="income-info__title">
				{{ translate("ui.regular_income_all") }}
			</div>
			<HorizontalDecorDivider />
			<div class="income-info__head">
				<div class="income-info__head-text">
					{{ translate("ui.income") }}
				</div>
				<div class="income-info__head-text">
					{{ translate("ui.in_storage") }}
				</div>
				<div class="income-info__head-text">
					{{ translate("ui.max_capacity") }}
				</div>
			</div>
			<IncomeInfoBox
				v-for="(income, i) in incomes"
				:key="i"
				:income="income"
				@toggle="setIncomeExpanded(income.type, $event)"
			/>
		</section>
		<section v-if="upkeep.length > 0">
			<div class="income-info__title">
				{{ translate("ui.upkeep") }}
			</div>
			<HorizontalDecorDivider />
			<div class="income-info__head">
				<div class="income-info__head-text">
					{{ translate("ui.reserve") }}
				</div>
				<div class="income-info__head-text">
					{{ translate("ui.consumption") }}
				</div>
				<div class="income-info__head-text">
					{{ translate("ui.total") }}
				</div>
			</div>
			<IncomeInfoBox
				v-for="(income, i) in upkeep"
				:key="i"
				:income="income"
				@toggle="setUpkeepExpanded(income.type, $event)"
			/>
		</section>
		<div class="offline-income-info">
			<div>
				<span>{{ translate("ui.offline_earnings") }}: </span>
				<span v-html="offlineIncomePercentHtml"> </span>
			</div>
			<div>
				<span>
					{{ translate("ui.max_offline_time") }}:
					<strong>{{
						`${translate("ui.x_minutes").replace("{number}", maxOfflineMinutes.toString())}`
					}}</strong>
				</span>
			</div>
		</div>
		<Teleport v-if="isMounted" to="#tab4-no-scroll">
			<section class="income-info__boost">
				<HorizontalDecorDivider />
				<div v-if="incomeBoostActive" class="income-info__boost-text">
					<div class="income-info__boost-text-success">
						{{ INCOME_BOOST_COEFFICIENT }}x
					</div>
					{{
						replacePlaceholder(
							translate("ui.triple_income_active"),
							"{number}",
							"",
						)
					}}
				</div>
				<div class="income-info__boost-time" v-if="incomeBoostActive">
					<Timer :time="boostTimeRemaining()" />
				</div>
				<div v-else class="income-info__boost-text">
					{{
						replacePlaceholder(
							translate("ui.triple_your_income"),
							"{number}",
							BOOST_DURATION,
						)
					}}
					<div
						:class="{
							'income-info__boost-text-danger':
								boostsRemaining <= 0,
						}"
					>
						{{
							`(${boostsRemaining}/${BOOST_MAX + getUpgradeCurrentReplaceValueById(UpgradeIds.witch_lake)})`
						}}
					</div>
				</div>
				<AppTextButton
					class="income-info__boost-btn"
					v-if="!incomeBoostActive"
					:key="incomeBoostTicksRemaining"
					:text="boostBtnText"
					:type="StatusTypes.PRIMARY"
					@click="boostIncomes"
					:disabled="boostsRemaining <= 0"
					:time-remaining="
						boostsRemaining <= 0
							? getTimeUntilTargetTime(
									userStore.accountEstablished,
								)
							: undefined
					"
					:notification="btnNotification"
				/>
			</section>
		</Teleport>
	</div>
</template>

<script lang="ts" setup>
import AppTextButton from "@/components/AppTextButton.vue";
import IncomeBoostRewardPopup from "@/components/IncomeBoostRewardPopup.vue";
import IncomeInfoBox from "@/components/Info/IncomeInfoBox.vue";
import HorizontalDecorDivider from "@/components/SVG/HorizontalDecorDivider.vue";
import Timer from "@/components/Timer.vue";
import {
	BOOST_DURATION,
	BOOST_MAX,
	BOOST_TICK_DURATION,
	FLAG_ENABLE_TIMERS,
	INCOME_BOOST_COEFFICIENT,
	TICK_DURATION,
} from "@/config/constants";
import {
	FrameName,
	tilerepoSettlementCastleBuildingsFrameNames,
} from "@/game/entities/config";
import { ResourceType } from "@/game/entities/config/resources";
import { UpgradeIds } from "@/game/entities/config/upgrades";
import type TileEntity from "@/game/entities/TileEntity";
import { getUnlocksByLevel } from "@/game/utils/resources";
import { FRAME_NAME_LEVEL_REGEXP } from "@/game/utils/settlement";
import { getUpgradeCurrentReplaceValueById } from "@/game/utils/upgrades";
import useEmitter, { EmitterEvents } from "@/hooks/useEmitter";
import useTileRepository from "@/hooks/useTileRepository";
import {
	PopupDictionary,
	PopupType,
	usePopupStore,
} from "@/store/usePopupStore";
import useResourcesStorageStore from "@/store/useResourcesStorageStore";
import useResourcesStore from "@/store/useResourcesStore";
import useRuntimeStore from "@/store/useRuntimeStore";
import useSettlementsStore from "@/store/useSettlementsStore";
import useUserStore from "@/store/useUserStore";
import { SkinTypes, StatusTypes } from "@/types/ui";
import { formatNumber } from "@/utils/format";
import { roundNumber } from "@/utils/number";
import { replacePlaceholder } from "@/utils/replacePlaceholder";
import { translate } from "@/utils/translate";
import { storeToRefs } from "pinia";
import { computed, markRaw, onBeforeUnmount, onMounted, ref } from "vue";

type ConsumptionSourceItem = {
	sourceText: string;
	count: number;
	consumption: number;
};

type Consumption = {
	type: string;
	value: number;
	storage: number;
	maxStorage: number | undefined;
	consumptionSources: Array<ConsumptionSourceItem>;
};

type IncomeSourceItem = {
	sourceText: string;
	count: number;
	production: number;
};

type Income = {
	type: string;
	value: number;
	storage: number;
	maxStorage: number | undefined;
	incomeSources: Array<IncomeSourceItem>;
};

const tilerepo = markRaw(useTileRepository());

const emitter = markRaw(useEmitter());
const userStore = useUserStore();
const { incomeBoostsUsedToday, incomeBoostTicksRemaining } =
	storeToRefs(userStore);

const runtimeStore = useRuntimeStore();
const { incomeBoostActive } = storeToRefs(runtimeStore);

const resourcesStore = useResourcesStore();
const resources = storeToRefs(resourcesStore);
const { coinsIncome, eventiumIncome } = resources;

const resourcesStorageStore = useResourcesStorageStore();

const btnNotification = ref<string>("");

const isMounted = ref<boolean>(false);

const boostBtnText = computed((): string => {
	if (boostsRemaining.value <= 0) {
		return translate("ui.available_in");
	}
	return replacePlaceholder(
		translate("button.multiply_income"),
		"{number}",
		INCOME_BOOST_COEFFICIENT.toString(),
	);
});

const calculatedUpkeep = ref<Array<Consumption>>([]);
const calculatedIncomes = ref<Array<Income>>([]);

const UPKEEP_RESOURCE_TYPES = [
	ResourceType.FOOD_CONSUMPTION,
	ResourceType.POPULATION_CONSUMPTION,
] as const;

const INCOME_RESOURCE_TYPES = [
	ResourceType.COINS_INCOME,
	ResourceType.LUMBER_INCOME,
	ResourceType.STONE_INCOME,
	ResourceType.CLAY_INCOME,
	ResourceType.IRON_INCOME,
	ResourceType.GOODS_INCOME,
	ResourceType.STEEL_INCOME,
	ResourceType.ARMY_INCOME,
	ResourceType.EVENTIUM_INCOME,
	ResourceType.RATIONS_INCOME,
] as const;

const INCOME_CONSUMPTION_RESOURCE_TYPES = [
	ResourceType.LUMBER_CONSUMPTION,
	ResourceType.STONE_CONSUMPTION,
	ResourceType.CLAY_CONSUMPTION,
	ResourceType.IRON_CONSUMPTION,
	ResourceType.GOODS_CONSUMPTION,
	ResourceType.STEEL_CONSUMPTION,
	ResourceType.ARMY_CONSUMPTION,
	ResourceType.FOOD_CONSUMPTION,
	ResourceType.POPULATION_CONSUMPTION,
	ResourceType.EVENTIUM_CONSUMPTION,
	ResourceType.RATIONS_CONSUMPTION,
] as const;

const resourceUnlocks = getUnlocksByLevel();
const settlementsStore = useSettlementsStore();

const isResourceUnlocked = (resourceType: ResourceType): boolean => {
	const unlockLevel = resourceUnlocks[resourceType];
	return !unlockLevel || settlementsStore.mainLevel >= unlockLevel;
};

const getIncomeValue = (
	resourceType: ResourceType,
	currency: ResourceType,
): number => {
	if (currency === ResourceType.COINS || currency === ResourceType.EVENTIUM) {
		return resources[resourceType]?.value ?? 0;
	}

	const uncappedIncome =
		resources[`${currency}UncappedIncomeWithoutPenalties` as ResourceType]
			?.value;
	if (uncappedIncome === undefined) {
		return resources[resourceType]?.value ?? 0;
	}

	const consumption =
		resources[`${currency}Consumption` as ResourceType]?.value ?? 0;
	return uncappedIncome - consumption;
};

const incomes = computed<Array<Income>>(
	(): Array<Income> =>
		INCOME_RESOURCE_TYPES.filter((resourceType: ResourceType): boolean => {
			const currency = resourceType
				.substring(0, resourceType.indexOf("Income"))
				.toLowerCase() as ResourceType;
			return isResourceUnlocked(currency);
		}).map((resourceType: ResourceType): Income => {
			const currency = resourceType
				.substring(0, resourceType.indexOf("Income"))
				.toLowerCase() as ResourceType;
			const calculatedIncome = calculatedIncomes.value.find(
				(income: Income): boolean => income.type === currency,
			);
			return {
				type: currency,
				value:
					calculatedIncome?.value ??
					getIncomeValue(resourceType, currency),
				storage: resources[currency]?.value ?? 0,
				maxStorage: resourcesStorageStore[currency] || undefined,
				incomeSources: calculatedIncome?.incomeSources ?? [],
			};
		}),
);

const upkeep = computed<Array<Consumption>>(
	(): Array<Consumption> =>
		UPKEEP_RESOURCE_TYPES.map((resourceType: ResourceType): Consumption => {
			const resourceCurrency =
				resourceType === ResourceType.FOOD_CONSUMPTION
					? ResourceType.FOOD_DIFF
					: ResourceType.POPULATION_DIFF;
			const currency = resourceType
				.substring(0, resourceType.indexOf("Consumption"))
				.replace("food", ResourceType.FOOD_DIFF)
				.replace("population", ResourceType.POPULATION_DIFF)
				.toLowerCase() as ResourceType;
			const calculatedConsumption = calculatedUpkeep.value.find(
				(consumption: Consumption): boolean =>
					consumption.type === currency,
			);
			return {
				type: currency,
				value:
					calculatedConsumption?.value ??
					resources[resourceType]?.value ??
					0,
				storage: resources[resourceCurrency]?.value ?? 0,
				maxStorage: calculatedConsumption?.maxStorage,
				consumptionSources:
					calculatedConsumption?.consumptionSources ?? [],
			};
		}),
);

enum CalculationKind {
	UPKEEP = "upkeep",
	INCOME = "income",
}

type ResourceCalculation =
	| {
			kind: CalculationKind.UPKEEP;
			resourceType: (typeof UPKEEP_RESOURCE_TYPES)[number];
	  }
	| {
			kind: CalculationKind.INCOME;
			resourceType: (typeof INCOME_RESOURCE_TYPES)[number];
	  };

const calculationQueue: Array<ResourceCalculation> = [];
const activeCalculations = new Map<string, ResourceCalculation>();
let groupedBuildingsForCalculation: Partial<
	Record<FrameName, Array<TileEntity>>
> = {};
let calculationTimer: ReturnType<typeof setTimeout> | undefined;

const getCalculationKey = (calculation: ResourceCalculation): string =>
	`${calculation.kind}:${calculation.resourceType}`;

const replaceCalculationResult = <T extends { type: string }>(
	results: Array<T>,
	result: T | undefined,
	type: string,
): void => {
	const resultIndex = results.findIndex(
		(item: T): boolean => item.type === type,
	);
	if (resultIndex >= 0) {
		if (result) results[resultIndex] = result;
		else results.splice(resultIndex, 1);
	} else if (result) {
		results.push(result);
	}
};

const scheduleNextCalculation = (): void => {
	if (calculationTimer || calculationQueue.length === 0) return;
	calculationTimer = setTimeout((): void => {
		calculationTimer = undefined;
		const calculation = calculationQueue.shift();
		if (!calculation) return;
		if (!activeCalculations.has(getCalculationKey(calculation))) {
			scheduleNextCalculation();
			return;
		}
		if (calculation.kind === CalculationKind.UPKEEP) {
			const result = upkeepCalculation(
				groupedBuildingsForCalculation,
				calculation.resourceType,
			)[0];
			replaceCalculationResult(
				calculatedUpkeep.value,
				result,
				result?.type ??
					calculation.resourceType
						.substring(
							0,
							calculation.resourceType.indexOf("Consumption"),
						)
						.toLowerCase(),
			);
		} else {
			const result = incomesCalcuation(
				groupedBuildingsForCalculation,
				calculation.resourceType,
			)[0];
			const resultType = calculation.resourceType
				.substring(0, calculation.resourceType.indexOf("Income"))
				.toLowerCase();
			replaceCalculationResult(
				calculatedIncomes.value,
				result,
				resultType,
			);
		}
		scheduleNextCalculation();
	}, 100);
};

const upkeepCalculation = (
	groupedBuildings: Partial<Record<FrameName, Array<TileEntity>>>,
	resourceType: (typeof UPKEEP_RESOURCE_TYPES)[number],
): Array<Consumption> => {
	if (FLAG_ENABLE_TIMERS) {
		// eslint-disable-next-line no-console
		console.time("upkeep");
	}
	const productionResourceTypes = [
		resourceType.replace("Consumption", "Income") as ResourceType,
	];
	const calculatedIncomes: Partial<Record<ResourceType, number>> = {};
	const calculatedIncomeSources: Partial<
		Record<
			ResourceType,
			Array<{ frameName: FrameName; production: number }>
		>
	> = {};

	for (const type of productionResourceTypes) {
		calculatedIncomes[type] = 0;
		calculatedIncomeSources[type] = [];
		const values = Object.values(groupedBuildings);
		for (const entities of values) {
			for (const entity of entities) {
				if (
					entity.production &&
					typeof entity.production.get === "function"
				) {
					const prod = entity.production.get(type);
					if (prod) {
						calculatedIncomes[type] += prod as number;
						calculatedIncomeSources[type].push({
							frameName: entity.frameName,
							production: prod as number,
						});
					}
				}
			}
		}
	}
	const consumptionResourceTypes = [resourceType];

	const calculatedConsumptions: Partial<Record<ResourceType, number>> = {};
	const calculatedStorages: Partial<Record<ResourceType, number>> = {};
	const calculatedConsumptionSources: Partial<
		Record<
			ResourceType,
			Array<{ frameName: FrameName; consumption: number }>
		>
	> = {};

	for (const type of consumptionResourceTypes) {
		calculatedConsumptions[type] = 0;
		calculatedStorages[type] = 0;
		calculatedConsumptionSources[type] = [];
		const values = Object.values(groupedBuildings);
		for (const entities of values) {
			for (const entity of entities) {
				if (
					entity.consumption &&
					typeof entity.consumption.get === "function"
				) {
					const consumption = entity.consumption.get(type);
					const storage = entity.storage?.get(
						type.substring(
							0,
							type.indexOf("Consumption"),
						) as ResourceType,
					);
					if (consumption) {
						calculatedConsumptions[type] += consumption;
						calculatedConsumptionSources[type].push({
							frameName: entity.frameName,
							consumption,
						});
					}
					if (storage) {
						calculatedStorages[type] += storage;
					}
				}
			}
		}
	}

	const entriesConsumption = Object.entries(calculatedConsumptions) as Array<
		[ResourceType, number]
	>;

	type ConsumptionSourceMapItem = {
		count: number;
		consumption: number;
		frameName: FrameName;
	};
	const result = entriesConsumption.map(
		([key, value]: [ResourceType, number]): Consumption => {
			let sourcesMap: Record<string, ConsumptionSourceMapItem> = {};
			for (const {
				frameName,
				consumption,
			} of calculatedConsumptionSources?.[key] || []) {
				const key = frameName.replace(
					FRAME_NAME_LEVEL_REGEXP,
					"$11$3",
				) as FrameName;
				if (!sourcesMap[key]) {
					sourcesMap[key] = {
						count: 0,
						consumption: 0,
						frameName: key,
					};
				}
				sourcesMap[key].count += 1;
				sourcesMap[key].consumption += consumption;
			}
			/// Find the corresponding consumption key for this income key
			const incomeKey = (
				key.endsWith("Consumption")
					? key.substring(0, key.indexOf("Consumption")) + "Income"
					: key
			) as ResourceType;
			for (const { frameName, production } of calculatedIncomeSources?.[
				incomeKey
			] || []) {
				let key: FrameName = frameName.replace(
					FRAME_NAME_LEVEL_REGEXP,
					"$11$3",
				) as FrameName;
				if (
					incomeKey === ResourceType.FOOD_INCOME &&
					tilerepoSettlementCastleBuildingsFrameNames.has(key)
				) {
					key = "ui.map_center" as FrameName;
				}
				if (!sourcesMap[key]) {
					sourcesMap[key] = {
						count: 0,
						consumption: 0,
						frameName: key,
					};
				}
				sourcesMap[key].count += 1;
				sourcesMap[key].consumption -= production;
			} // in sourcemap group all sources with frameName including "GRAVEL_ROAD" to FrameName.GRAVEL_ROAD, "COBBLED_ROAD" to FrameName.COBBLED_ROAD, "WOODEN_BRIDGE" to FrameName.WOODEN_BRIDGE, "STONE_BRIDGE" to FrameName.STONE_BRIDGE
			const mergeKeys = [
				{
					regexp: /^fish_trap/i,
					target: FrameName.SHALLOW_SEA_SMALL_FISH_TRAP,
				},
				{
					regexp: /^fishing_boat/i,
					target: FrameName.SHALLOW_SEA_FISHING_BOAT,
				},
				{
					regexp: /^big_fishing_boat/i,
					target: FrameName.SHALLOW_SEA_BIG_FISHING_BOAT,
				},
				{
					regexp: /^pearl_hunter/i,
					target: FrameName.SHALLOW_SEA_PEARL_HUNTER,
				},
				{
					regexp: /^scout_tower/i,
					target: FrameName.HILLS_SCOUT_TOWER,
				},
			];

			for (const { regexp, target } of mergeKeys) {
				const keysToMerge = (
					Object.keys(sourcesMap) as Array<string>
				).filter(
					(key: string): boolean =>
						regexp.test(key) && key !== target,
				);
				if (keysToMerge.length) {
					if (!sourcesMap[target]) {
						sourcesMap[target] = {
							count: 0,
							consumption: 0,
							frameName: target,
						};
					}
					for (const k of keysToMerge) {
						sourcesMap[target].count += sourcesMap[k].count;
						sourcesMap[target].consumption +=
							sourcesMap[k].consumption;
						delete sourcesMap[k];
					}
				}
			}
			// order sourceMap by consumption desc
			const sortedSourcesMap: typeof sourcesMap = {};
			for (const [sourceKey, source] of Object.entries(sourcesMap).sort(
				(
					a: [string, (typeof sourcesMap)[string]],
					b: [string, (typeof sourcesMap)[string]],
				): number => b[1].consumption - a[1].consumption,
			)) {
				sortedSourcesMap[sourceKey] = source;
			}
			sourcesMap = sortedSourcesMap;
			const consumptionSources = Object.values(sourcesMap).map(
				({
					frameName,
					count,
					consumption,
				}: ConsumptionSourceMapItem): ConsumptionSourceItem => ({
					sourceText:
						// check if frameName is a value of FrameName enum
						Object.values(FrameName).includes(
							frameName as FrameName,
						)
							? `${frameName}.name`
							: frameName,
					count: count,
					consumption: consumption,
				}),
			);
			return {
				type: key
					.substring(0, key.indexOf("Consumption"))
					.replace("food", ResourceType.FOOD_DIFF.toString())
					.replace(
						"population",
						ResourceType.POPULATION_DIFF.toString(),
					)
					.toLowerCase(),
				value,
				storage: resourcesStore.getCurrencyValue(
					key
						.substring(0, key.indexOf("Consumption"))
						.toLowerCase()
						.replace("food", ResourceType.FOOD_DIFF.toString())
						.replace(
							"population",
							ResourceType.POPULATION_DIFF.toString(),
						) as ResourceType,
				),
				maxStorage: calculatedStorages?.[key] || undefined,
				consumptionSources,
			};
		},
	);
	if (FLAG_ENABLE_TIMERS) {
		// eslint-disable-next-line no-console
		console.timeEnd("upkeep");
	}
	return result;
};

const incomesCalcuation = (
	groupedBuildings: Partial<Record<FrameName, Array<TileEntity>>>,
	resourceType: (typeof INCOME_RESOURCE_TYPES)[number],
): Array<Income> => {
	if (FLAG_ENABLE_TIMERS) {
		// eslint-disable-next-line no-console
		console.time("income");
	}
	const matchingConsumptionType = resourceType.replace(
		"Income",
		"Consumption",
	) as ResourceType;
	const consumptionResourceTypes = INCOME_CONSUMPTION_RESOURCE_TYPES.filter(
		(type: ResourceType): boolean => type === matchingConsumptionType,
	);

	const calculatedConsumptions: Partial<Record<ResourceType, number>> = {};
	const calculatedConsumptionSources: Partial<
		Record<
			ResourceType,
			Array<{ frameName: FrameName; consumption: number }>
		>
	> = {};

	for (const type of consumptionResourceTypes) {
		calculatedConsumptions[type] = 0;
		calculatedConsumptionSources[type] = [];
		const values = Object.values(groupedBuildings);
		for (const entities of values) {
			for (const entity of entities) {
				if (
					entity.consumption &&
					typeof entity.consumption.get === "function"
				) {
					const consumption = entity.consumption.get(type);
					if (consumption) {
						calculatedConsumptions[type] += consumption;
						calculatedConsumptionSources[type].push({
							frameName: entity.frameName,
							consumption,
						});
					}
				}
			}
		}
	}

	const entriesConsumption = Object.entries(calculatedConsumptions) as Array<
		[ResourceType, number]
	>;
	const productionResourceTypes = [resourceType];

	const calculatedIncomes: Partial<Record<ResourceType, number>> = {};
	const calculatedStorages: Partial<Record<ResourceType, number>> = {};
	const calculatedIncomeSources: Partial<
		Record<
			ResourceType,
			Array<{ frameName: FrameName; production: number }>
		>
	> = {};

	for (const type of productionResourceTypes) {
		calculatedIncomes[type] = 0;
		calculatedStorages[type] = 0;
		calculatedIncomeSources[type] = [];
		const values = Object.values(groupedBuildings);
		for (const entities of values) {
			for (const entity of entities) {
				const prod = entity.production.get(type);
				const storage = entity.storage?.get(
					type.substring(0, type.indexOf("Income")) as ResourceType,
				);
				if (prod) {
					calculatedIncomes[type] += prod as number;
					calculatedIncomeSources[type].push({
						frameName: entity.frameName,
						production: prod as number,
					});
				}
				if (storage) {
					calculatedStorages[type] += storage;
				}
			}
		}
	}
	if (!calculatedIncomeSources[ResourceType.COINS_INCOME]) {
		calculatedIncomeSources[ResourceType.COINS_INCOME] = [];
	}
	if (!calculatedIncomeSources[ResourceType.EVENTIUM_INCOME]) {
		calculatedIncomeSources[ResourceType.EVENTIUM_INCOME] = [];
	}

	// pridaj 1 coin za kazde odhalene policko, odpocitaj vodu, ta ma zisk podla upgradu naval_trade
	const {
		exploredTileCount,
		exploredWaterTileCount,
		exploredWaterTileIncome,
	} = tilerepo;
	const totalTileIncome =
		exploredTileCount - exploredWaterTileCount + exploredWaterTileIncome;

	for (let i = 0; i < totalTileIncome; i++) {
		calculatedIncomeSources[ResourceType.COINS_INCOME].push({
			frameName: "ui.tiles_explored" as FrameName,
			production: 1,
		});
	}

	const otherIncome =
		(coinsIncome.value ?? 0) -
		totalTileIncome -
		(calculatedIncomes[ResourceType.COINS_INCOME] || 0);
	if (otherIncome > 0) {
		calculatedIncomeSources[ResourceType.COINS_INCOME].push(
			...Array(1).fill({
				frameName: "ui.other",
				production: otherIncome,
			}),
		);
	}
	const otherEventiumIncome =
		(eventiumIncome.value ?? 0) -
		(calculatedIncomes[ResourceType.EVENTIUM_INCOME] || 0);
	if (otherEventiumIncome > 0) {
		calculatedIncomeSources[ResourceType.EVENTIUM_INCOME].push(
			...Array(1).fill({
				frameName: "ui.other",
				production: otherEventiumIncome,
			}),
		);
	}
	let entriesIncome = Object.entries(calculatedIncomes) as Array<
		[ResourceType, number]
	>;
	const unlocks = getUnlocksByLevel();
	// Prepare filtered and updated entriesIncome
	const mainLevel = useSettlementsStore().mainLevel;
	entriesIncome = entriesIncome
		.map(
			([key, income]: [ResourceType, number]): [ResourceType, number] => {
				// Subtract consumption of the same resource type from income
				const matchingConsumption = entriesConsumption.find(
					([consKey]: [ResourceType, number]): boolean =>
						consKey
							.substring(0, consKey.indexOf("Consumption"))
							.toLowerCase() ===
						key.substring(0, key.indexOf("Income")).toLowerCase(),
				)?.[1] as number | undefined;
				const newIncome =
					typeof matchingConsumption === "number"
						? income - matchingConsumption
						: income;
				return [key, newIncome] as [ResourceType, number];
			},
		)
		.filter(([key]: [ResourceType, number]): boolean => {
			const resourceType = key
				.substring(0, key.indexOf("Income"))
				.toLowerCase();
			const unlockLevel = unlocks[resourceType as ResourceType];
			return !(unlockLevel && mainLevel < unlockLevel);
		});

	type IncomeSourceMapItem = {
		count: number;
		production: number;
		frameName: FrameName;
	};
	entriesIncome = entriesIncome.map(
		([key, value]: [ResourceType, number]): [ResourceType, number] => {
			if (key === ResourceType.COINS_INCOME) {
				value = coinsIncome.value ?? 0;
			}
			if (key === ResourceType.EVENTIUM_INCOME) {
				value = eventiumIncome.value ?? 0;
			}
			return [key, value] as [ResourceType, number];
		},
	);
	const result = entriesIncome.map(
		([key, value]: [ResourceType, number]): Income => {
			let sourcesMap: Record<
				string,
				{ count: number; production: number; frameName: FrameName }
			> = {};
			for (const { frameName, production } of calculatedIncomeSources?.[
				key
			] || []) {
				const key = frameName.replace(
					FRAME_NAME_LEVEL_REGEXP,
					"$11$3",
				) as FrameName;
				if (!sourcesMap[key]) {
					sourcesMap[key] = {
						count: 0,
						production: 0,
						frameName: key,
					};
				}
				sourcesMap[key].count += 1;
				sourcesMap[key].production += production;
			}
			// Find the corresponding consumption key for this income key
			const consumptionKey = (
				key.endsWith("Income")
					? key.substring(0, key.indexOf("Income")) + "Consumption"
					: key
			) as ResourceType;

			for (const {
				frameName,
				consumption,
			} of calculatedConsumptionSources?.[consumptionKey] || []) {
				const key = frameName.replace(
					FRAME_NAME_LEVEL_REGEXP,
					"$11$3",
				) as FrameName;
				if (!sourcesMap[key]) {
					sourcesMap[key] = {
						count: 0,
						production: 0,
						frameName: key,
					};
				}
				sourcesMap[key].count += 1;
				sourcesMap[key].production -= consumption;
			}
			// in sourcemap group all sources with frameName including "GRAVEL_ROAD" to FrameName.GRAVEL_ROAD, "COBBLED_ROAD" to FrameName.COBBLED_ROAD, "WOODEN_BRIDGE" to FrameName.WOODEN_BRIDGE, "STONE_BRIDGE" to FrameName.STONE_BRIDGE
			const mergeKeys = [
				{ match: "road_gravel", target: FrameName.GRAVEL_ROAD },
				{ match: "road_cobbled", target: FrameName.COBBLED_ROAD },
				{ match: "bridge_wooden", target: FrameName.WOODEN_BRIDGE },
				{ match: "bridge_stone", target: FrameName.STONE_BRIDGE },
			];

			for (const { match, target } of mergeKeys) {
				const keysToMerge = Object.keys(sourcesMap).filter(
					(k: string): boolean => {
						return (
							k.toLowerCase().includes(match.toLowerCase()) &&
							k.toLowerCase() !== target.toLowerCase()
						);
					},
				);
				if (keysToMerge.length) {
					if (!sourcesMap[target]) {
						sourcesMap[target] = {
							count: 0,
							production: 0,
							frameName: target,
						};
					}
					for (const k of keysToMerge) {
						sourcesMap[target].count += sourcesMap[k].count;
						sourcesMap[target].production +=
							sourcesMap[k].production;
						delete sourcesMap[k];
					}
				}
			}
			// order sourceMap by production desc
			const sortedSourcesMap: typeof sourcesMap = {};
			for (const [sourceKey, source] of Object.entries(sourcesMap).sort(
				(
					a: [string, (typeof sourcesMap)[string]],
					b: [string, (typeof sourcesMap)[string]],
				): number => b[1].production - a[1].production,
			)) {
				sortedSourcesMap[sourceKey] = source;
			}
			sourcesMap = sortedSourcesMap;

			const incomeSources = Object.values(sourcesMap).map(
				({
					frameName,
					count,
					production,
				}: IncomeSourceMapItem): IncomeSourceItem => ({
					sourceText:
						// check if frameName is a value of FrameName enum
						Object.values(FrameName).includes(
							frameName as FrameName,
						)
							? `${frameName}.name`
							: frameName,
					count: count,
					production: production,
				}),
			);

			const formattedKey = key
				.substring(0, key.indexOf("Income"))
				.replace("food", ResourceType.FOOD_DIFF.toString())
				.replace("population", ResourceType.POPULATION_DIFF.toString())
				.toLowerCase() as ResourceType;

			return {
				type: formattedKey,
				value,
				storage: resourcesStore.getCurrencyValue(formattedKey),
				maxStorage: resourcesStorageStore[formattedKey] || undefined,
				incomeSources,
			};
		},
	);
	if (FLAG_ENABLE_TIMERS) {
		// eslint-disable-next-line no-console
		console.timeEnd("income");
	}
	return result;
};

const boostsRemaining = computed((): number => {
	return (
		BOOST_MAX +
		getUpgradeCurrentReplaceValueById(UpgradeIds.witch_lake) -
		(incomeBoostsUsedToday.value || 0)
	);
});
const boostIncomes = (): void => {
	if (incomeBoostActive.value) return;

	const popupStore = usePopupStore();

	popupStore.showPopup({
		id: PopupDictionary.INCOME_BOOST_AD,
		type: PopupType.REWARDED_AD,
		heading: translate("ui.rewarded_ad"),
		width: 24,
		maxHeight: 24,
		percentageWidth: "95%",
		skin: SkinTypes.LIGHT,
		isPaddingless: true,
		closeable: false,
		component: markRaw(IncomeBoostRewardPopup),
	});
};

const boostTimeRemaining = (): number => {
	if (!incomeBoostActive.value) return 0;
	if (incomeBoostTicksRemaining.value === BOOST_TICK_DURATION) {
		return incomeBoostTicksRemaining.value * TICK_DURATION;
	}
	const secondsModifier =
		+(
			document
				.getElementById("game-ticker-progress-bar-inner")
				?.style.width.slice(0, -1) || 0
		) / 10;
	return incomeBoostTicksRemaining.value * TICK_DURATION - secondsModifier;
};

const getTimeUntilTargetTime = (targetDate: Date): number => {
	const now = new Date();
	const targetTimeToday = new Date(
		now.getFullYear(),
		now.getMonth(),
		now.getDate(),
		targetDate.getHours(),
		targetDate.getMinutes(),
		targetDate.getSeconds(),
		targetDate.getMilliseconds(),
	);
	let timeDifference = targetTimeToday.getTime() - now.getTime();
	if (timeDifference < 0) {
		const oneDayInMilliseconds = 24 * 60 * 60 * 1000;
		timeDifference += oneDayInMilliseconds;
	}

	return timeDifference / 1000;
};
const maxOfflineMinutes = computed((): string =>
	formatNumber(Math.trunc(tilerepo.maxOfflineTime() / 60 / 1_000)),
);

const offlinePercentage = computed((): string => {
	return roundNumber(tilerepo.offlinePercentage() * 100) + "%";
});
const offlineIncomePercentHtml = computed(() => {
	return translate("ui.offline_income_percent").replace(
		"{percent}",
		`<strong>${offlinePercentage.value}</strong>`,
	);
});

const recalculate = (calculations: Array<ResourceCalculation>): void => {
	if (calculations.length === 0) return;
	if (FLAG_ENABLE_TIMERS) {
		// eslint-disable-next-line no-console
		console.time("recalculate");
	}
	const buildings: Array<TileEntity> = Array.from(
		tilerepo.getAllProductionEntities(),
	);
	const groupedBuildings: Partial<Record<FrameName, Array<TileEntity>>> =
		buildings.reduce(
			(
				acc: Partial<Record<FrameName, Array<TileEntity>>>,
				entity: TileEntity,
			): Partial<Record<FrameName, Array<TileEntity>>> => {
				if (entity.frameName in acc)
					acc[entity.frameName]!.push(entity);
				else acc[entity.frameName] = [entity];

				return acc;
			},
			{} as Partial<Record<FrameName, Array<TileEntity>>>,
		);
	groupedBuildingsForCalculation = groupedBuildings;
	for (const calculation of calculations) {
		if (!activeCalculations.has(getCalculationKey(calculation))) continue;
		if (
			!calculationQueue.some(
				(queuedCalculation: ResourceCalculation): boolean =>
					getCalculationKey(queuedCalculation) ===
					getCalculationKey(calculation),
			)
		) {
			calculationQueue.push(calculation);
		}
	}
	if (FLAG_ENABLE_TIMERS) {
		// eslint-disable-next-line no-console
		console.timeEnd("recalculate");
	}
	scheduleNextCalculation();
};

const recalculateFood = (): void => {
	const foodCalculations = [...activeCalculations.values()].filter(
		(calculation: ResourceCalculation): boolean =>
			calculation.kind === CalculationKind.UPKEEP &&
			calculation.resourceType === ResourceType.FOOD_CONSUMPTION,
	);
	recalculate(foodCalculations);
};

const setIncomeExpanded = (type: string, isExpanded: boolean): void => {
	const resourceType = INCOME_RESOURCE_TYPES.find(
		(candidate: string): boolean =>
			candidate
				.substring(0, candidate.indexOf("Income"))
				.toLowerCase() === type,
	);
	if (!resourceType) return;
	const calculation: ResourceCalculation = {
		kind: CalculationKind.INCOME,
		resourceType,
	};
	const key = getCalculationKey(calculation);
	if (!isExpanded) {
		activeCalculations.delete(key);
		return;
	}
	activeCalculations.set(key, calculation);
	recalculate([calculation]);
};

const setUpkeepExpanded = (type: string, isExpanded: boolean): void => {
	const resourceType = UPKEEP_RESOURCE_TYPES.find(
		(candidate: string): boolean =>
			candidate
				.substring(0, candidate.indexOf("Consumption"))
				.replace("food", ResourceType.FOOD_DIFF)
				.replace("population", ResourceType.POPULATION_DIFF)
				.toLowerCase() === type,
	);
	if (!resourceType) return;
	const calculation: ResourceCalculation = {
		kind: CalculationKind.UPKEEP,
		resourceType,
	};
	const key = getCalculationKey(calculation);
	if (!isExpanded) {
		activeCalculations.delete(key);
		return;
	}
	activeCalculations.set(key, calculation);
	recalculate([calculation]);
};

onMounted((): void => {
	emitter.on(EmitterEvents.GameTick, recalculateFood);
	if (useRuntimeStore().infoNotifications > 0) {
		btnNotification.value = "!";
	}
	useRuntimeStore().setInfoNotifications(0);

	isMounted.value = true;
});

onBeforeUnmount((): void => {
	// Clean up event listener
	emitter.off(EmitterEvents.GameTick, recalculateFood);
	if (calculationTimer) clearTimeout(calculationTimer);
	calculationTimer = undefined;
	calculationQueue.length = 0;
});
</script>

<style lang="scss" scoped>
.income-info {
	width: 100%;
	@include flex-col-start;
	position: relative;
	gap: 1rem;
	padding: 0 0.5rem;
	& > section {
		width: 100%;
		@include flex-col-start;
		gap: 0.5rem;
	}
	&__title {
		padding-top: 0.5rem;
		width: 100%;
		color: #f4d278;
		font-weight: bold;
		font-size: 1.25rem;
		line-height: 1;
		text-align: center;
		text-transform: uppercase;
	}
	&__head {
		display: flex;
		align-items: center;
		padding-right: 2rem;
		width: 100%;
		& > div {
			width: 33.3333%;
			color: #fff3dd;
			font-weight: bold;
			font-size: 1rem;
			text-align: center;
		}
	}

	:deep(.horizontal-decor-divider) {
		margin: 0 auto;
		width: 100%;
	}
}

.offline-income-info {
	@include flex-col-center;
	color: #f4d278;
	font-size: 0.8125rem;
	text-shadow: 0.044rem 0.044rem 0.063rem rgba(0, 0, 0, 0.6);
}
.income-info__boost {
	@include flex-col-center;
	padding-bottom: 1.25rem;
	&-text {
		display: flex;
		gap: 0.15rem;
		padding: 0.25rem 0;
		color: #fff3dd;
		font-weight: bold;
		& > div {
			font-weight: bold;
			&.income-info__boost-text-danger {
				color: #ff3d3d;
			}
			&.income-info__boost-text-success {
				color: #19b345;
			}
		}
	}
	&-time {
		color: #f2dcb5;
		font-weight: 800;
		@include flex-row-center;
		gap: 0.15rem;
	}
	&-btn {
		margin-top: 0.5rem;
	}
}
</style>
